/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import { Studio } from "@/components/studio/Studio";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useSearch } from "@/hooks/useSearch";
import {
  processDataPackage,
  runProcessingFromFiles,
} from "@/lib/process-package";
import type {
  FlowMode,
  FlowPreferences,
  PackageStats,
  ProcessedChannel,
  ProcessingError,
} from "@/types";
import type { FileWithHandle } from "browser-fs-access";
import { directoryOpen } from "browser-fs-access";
import clsx from "clsx";
import { CaretRight, Code, Lightning, Shield } from "phosphor-react";
import { useState } from "react";

type FlowStep = "choice" | "safe-flow" | "risky-flow" | "studio";
type RiskyStep = "warning" | "setup";

interface AppState {
  currentStep: FlowStep;
  channels: ProcessedChannel[];
  stats: PackageStats | null;
  loading: boolean;
  error: ProcessingError | null;
  flowMode: FlowMode;
  safePreferences: FlowPreferences;
  riskyPreferences: FlowPreferences;
}

interface FlowViewProps {
  onBack: () => void;
  onSelectFolder: () => Promise<void>;
  loading: boolean;
  error: string | null;
  preferences: FlowPreferences;
  onToggleAccessible: (value: boolean) => void;
}

interface SetupStep {
  title: string;
  description: string;
  actionLabel?: string;
  actionUrl?: string;
}

const PageContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex min-h-screen items-start justify-center overflow-hidden p-6 md:p-10">
    <div className="w-full max-w-2xl space-y-8 text-left">{children}</div>
  </div>
);

const IconHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  centered?: boolean;
}> = ({ icon, title, subtitle, centered = true }) => (
  <div className={clsx("space-y-4", centered ? "text-center" : "text-left")}>
    {icon}
    <h2 className="font-lato text-4xl font-light tracking-wide text-slate-900 dark:text-slate-100">
      {title}
    </h2>
    <p className="font-lato text-lg text-slate-700 dark:text-slate-300">
      {subtitle}
    </p>
  </div>
);

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div
    className={clsx(
      "rounded-xl border border-slate-300 bg-slate-100 text-left shadow-sm dark:border-slate-700 dark:bg-slate-900",
      className,
    )}
  >
    {children}
  </div>
);

const CardSection: React.FC<{
  children: React.ReactNode;
  withBorder?: boolean;
  className?: string;
}> = ({ children, withBorder = false, className }) => (
  <div
    className={clsx(
      withBorder ? "border-t border-slate-200 dark:border-slate-700" : "",
      className,
    )}
  >
    <div className="p-8">{children}</div>
  </div>
);

const InfoSection: React.FC<{
  heading: string;
  headingLevel?: "h3" | "h4";
  withBorder?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({
  heading,
  headingLevel = "h4",
  withBorder = false,
  className,
  children,
}) => (
  <CardSection
    withBorder={withBorder}
    className={clsx("space-y-3 text-left", className)}
  >
    {headingLevel === "h3" ? (
      <h3 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
        {heading}
      </h3>
    ) : (
      <h4 className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
        {heading}
      </h4>
    )}
    <div className="space-y-3 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
      {children}
    </div>
  </CardSection>
);

const BackButton: React.FC<{ onClick: () => void; label?: string }> = ({
  onClick,
  label = "← Back to choice",
}) => (
  <button
    onClick={onClick}
    className="font-lato cursor-pointer text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
  >
    {label}
  </button>
);

const SetupStepItem: React.FC<{ step: SetupStep; stepNumber: number }> = ({
  step,
  stepNumber,
}) => (
  <div className="space-y-3">
    <h3 className="font-lato text-lg font-medium text-slate-900 dark:text-slate-100">
      Step {stepNumber}: {step.title}
    </h3>
    <p className="font-lato text-sm text-slate-700 dark:text-slate-300">
      {step.description}
    </p>
    {step.actionUrl && step.actionLabel && (
      <a
        href={step.actionUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="font-lato inline-block cursor-pointer rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700"
      >
        {step.actionLabel}
      </a>
    )}
  </div>
);

const createInitialState = (): AppState => ({
  currentStep: "choice",
  channels: [],
  stats: null,
  loading: false,
  error: null,
  flowMode: null,
  safePreferences: {
    includeAccessibleResults: false,
  },
  riskyPreferences: {
    includeAccessibleResults: true,
  },
});

const initialState = createInitialState();

export function App() {
  const [state, setState] = useState<AppState>(initialState);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSelectMessagesFolder = async () => {
    setState((prev) => ({ ...prev, error: null }));

    try {
      const selectedFolder = await directoryOpen({
        mode: "read",
        recursive: true,
      });

      setIsProcessing(true);
      setState((prev) => ({ ...prev, loading: true }));

      let channels: ProcessedChannel[];
      let stats: PackageStats;

      if (Array.isArray(selectedFolder)) {
        const files = selectedFolder as FileWithHandle[];
        const result = await runProcessingFromFiles(files as File[]);
        channels = result.channels;
        stats = result.stats;
      } else {
        let messagesFolder = selectedFolder as FileSystemDirectoryHandle;

        if (
          (selectedFolder as FileSystemDirectoryHandle).name.toLowerCase() !==
          "messages"
        ) {
          try {
            messagesFolder = await (
              selectedFolder as FileSystemDirectoryHandle
            ).getDirectoryHandle("Messages", {
              create: false,
            });
          } catch {
            throw new Error(
              'Please point to the extracted "Messages" folder from your Discord data package.',
            );
          }
        }

        const result = await processDataPackage(messagesFolder);
        channels = result.channels;
        stats = result.stats;
      }

      setIsProcessing(false);
      setState((prev) => ({
        ...prev,
        channels,
        stats,
        currentStep: "studio",
        loading: false,
      }));
    } catch (err) {
      setIsProcessing(false);
      if (
        typeof DOMException !== "undefined" &&
        err instanceof DOMException &&
        err.name === "AbortError"
      ) {
        return;
      }

      const message =
        err instanceof Error
          ? err.message
          : "Failed to read the selected folder. Make sure you granted access.";

      setState((prev) => ({
        ...prev,
        error: {
          type: "FS_ERROR",
          message,
          details: err,
        },
        loading: false,
      }));
    }
  };

  const handleSetCurrentStep = (step: FlowStep) => {
    setState((prev) => ({
      ...prev,
      currentStep: step,
      flowMode:
        step === "safe-flow"
          ? "safe"
          : step === "risky-flow"
            ? "risky"
            : step === "choice"
              ? null
              : prev.flowMode,
    }));
  };

  const resetAppState = () => {
    setState(createInitialState());
    useSearch.getState().reset();
  };

  const handleBackToChoice = () => {
    resetAppState();
  };

  const handleToggleSafeAccessible = (value: boolean) => {
    setState((prev) => ({
      ...prev,
      safePreferences: {
        ...prev.safePreferences,
        includeAccessibleResults: value,
      },
    }));
  };

  const handleToggleRiskyAccessible = (value: boolean) => {
    setState((prev) => ({
      ...prev,
      riskyPreferences: {
        ...prev.riskyPreferences,
        includeAccessibleResults: value,
      },
    }));
  };

  const handleSetCurrentScreen = (screen: "landing" | "studio") => {
    if (screen === "landing") {
      resetAppState();
      return;
    }

    setState((prev) => ({ ...prev, currentStep: "studio" }));
  };

  if (state.currentStep === "studio") {
    if (!state.stats) throw new Error("Stats not available for Studio view");

    const selectedPreferences =
      state.flowMode === "safe"
        ? state.safePreferences
        : state.flowMode === "risky"
          ? state.riskyPreferences
          : null;

    return (
      <Studio
        channels={state.channels}
        stats={state.stats}
        setCurrentScreen={handleSetCurrentScreen}
        flowMode={state.flowMode}
        flowPreferences={selectedPreferences}
      />
    );
  }

  return (
    <div className="font-lato min-h-screen overflow-hidden py-8">
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      {state.currentStep === "choice" && (
        <ChoiceView onSetStep={handleSetCurrentStep} />
      )}

      {state.currentStep === "safe-flow" && (
        <SafeFlowView
          onBack={handleBackToChoice}
          onSelectFolder={handleSelectMessagesFolder}
          loading={isProcessing}
          error={state.error?.message ?? null}
          preferences={state.safePreferences}
          onToggleAccessible={handleToggleSafeAccessible}
        />
      )}

      {state.currentStep === "risky-flow" && (
        <RiskyFlowView
          onBack={handleBackToChoice}
          onSelectFolder={handleSelectMessagesFolder}
          loading={isProcessing}
          error={state.error?.message ?? null}
          preferences={state.riskyPreferences}
          onToggleAccessible={handleToggleRiskyAccessible}
        />
      )}
    </div>
  );
}

const ChoiceView: React.FC<{ onSetStep: (step: FlowStep) => void }> = ({
  onSetStep,
}) => (
  <div className="flex min-h-screen items-center justify-center overflow-hidden p-6">
    <div className="w-full max-w-2xl space-y-8 text-center">
      <div className="space-y-3">
        <h1 className="text-4xl font-black tracking-tight text-slate-900 dark:text-slate-100">
          Discorch
        </h1>
        <p className="mx-auto max-w-lg text-base text-slate-700 dark:text-slate-300">
          Browse your Discord history and request message deletions
        </p>
      </div>

      <div className="rounded-xl border-2 border-blue-600 bg-blue-50 dark:border-blue-500 dark:bg-blue-950">
        <div className="p-6">
          <div className="space-y-2 text-sm leading-relaxed text-blue-800 dark:text-blue-200">
            <p>
              This tool works entirely offline and does not submit your data to
              any servers. It does not require you to log in with your Discord
              account.
            </p>
            <p>
              It merely helps you navigate your data package from Discord and
              craft data deletion requests to send to their privacy team. In
              cases where their privacy team's policies prohibit deletion, it
              guides you through automating the remaining tasks yourself.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <button
          onClick={() => onSetStep("safe-flow")}
          className="group relative cursor-pointer overflow-hidden rounded-xl border-2 border-green-600 bg-green-50 text-left transition-colors hover:bg-green-100/70 dark:border-green-500 dark:bg-green-950 dark:hover:bg-green-900/70"
        >
          <div className="p-8">
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-4">
                <Shield
                  size={32}
                  className="text-green-700 dark:text-green-400"
                />
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-green-900 dark:text-green-100">
                    Play it safe
                  </h3>
                  <p className="text-sm leading-relaxed text-green-800 dark:text-green-200">
                    Follow Discord's terms. Only delete messages from servers
                    and group chats you can no longer access.
                  </p>
                </div>
              </div>
              <CaretRight
                size={20}
                className="mt-2 shrink-0 text-green-600 dark:text-green-500"
              />
            </div>
          </div>
        </button>

        <button
          onClick={() => onSetStep("risky-flow")}
          className="group relative cursor-pointer overflow-hidden rounded-xl border-2 border-red-600 bg-red-50 text-left transition-colors hover:bg-red-100/70 dark:border-red-500 dark:bg-red-950 dark:hover:bg-red-900/70"
        >
          <div className="p-8">
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-4">
                <Lightning
                  size={32}
                  className="text-red-700 dark:text-red-400"
                />
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-red-900 dark:text-red-100">
                    Live on the edge
                  </h3>
                  <p className="text-sm leading-relaxed text-red-800 dark:text-red-200">
                    Understand the risks. Automate deletion of 1:1 DMs against
                    Discord's terms.
                  </p>
                </div>
              </div>
              <CaretRight
                size={20}
                className="mt-2 shrink-0 text-red-600 dark:text-red-500"
              />
            </div>
          </div>
        </button>
      </div>

      <div className="mt-6 space-y-4 text-left">
        <div className="mx-auto w-full max-w-xl">
          <Card>
            <InfoSection heading="How this works" headingLevel="h3">
              <p>
                Discord acts in malicious compliance with the GDPR. You are not
                allowed to delete your DMs through their support flow, since you
                can always regain access to a DM, technically speaking, since
                you are always a member of the DM channel on their backend.
              </p>
              <p>
                For servers and group DMs you no longer have access to, however,
                Discord will comply with deletion requests. This tool helps you
                identify those messages and craft deletion requests for their
                privacy team.
              </p>
              <p>
                For 1:1 DMs, Discord will not delete them through the privacy
                request process. In that case, the risky flow guides you through
                automating the deletion yourself via browser extensions.
              </p>
            </InfoSection>

            <InfoSection heading="Getting your data package" withBorder>
              <p>
                Both options rely on the extracted "Messages" folder from your
                Discord data package. After unzipping, use the file picker to
                open that directory directly in your browser.
              </p>
              <p>
                The tool operates entirely offline and only indexes the Messages
                folder.
              </p>
              <a
                href="https://support.discord.com/hc/en-us/articles/360004957991-Your-Discord-Data-Package"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                <span>→ Discord data package export guide</span>
              </a>
            </InfoSection>

            <InfoSection heading="Privacy and security" withBorder>
              <p>
                You can disable your internet connection to be extra sure we are
                not doing anything with your data package.
              </p>
              <p>The code is also fully open source for your verification.</p>
            </InfoSection>
          </Card>
        </div>

        <div className="flex justify-center pt-3">
          <a
            href="https://codeberg.org/discorch/discorch"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex cursor-pointer items-center gap-2 rounded-xl border-2 border-slate-400 bg-slate-200 px-4 py-3 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-300/70 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700/70"
          >
            <Code size={14} />
            <span>View source on Codeberg</span>
          </a>
        </div>
      </div>
    </div>
  </div>
);

const SafeFlowView: React.FC<FlowViewProps> = ({
  onBack,
  onSelectFolder,
  loading,
  error,
  preferences,
  onToggleAccessible,
}) => (
  <PageContainer>
    <IconHeader
      icon={
        <Shield
          size={48}
          className="mx-auto text-slate-600 dark:text-slate-400"
        />
      }
      title="Safe and compliant"
      subtitle="This approach follows Discord's terms of service completely"
      centered
    />

    <div className="mx-auto w-full max-w-xl">
      <Card>
        <CardSection className="space-y-3 border-b border-slate-200 text-left dark:border-slate-700">
          <div className="flex items-center gap-3">
            <Checkbox
              id="safe-include-accessible"
              checked={preferences.includeAccessibleResults}
              onCheckedChange={(checked) => onToggleAccessible(!!checked)}
            />
            <div>
              <Label
                htmlFor="safe-include-accessible"
                className="text-sm font-medium text-slate-900 dark:text-slate-100"
              >
                Show 1:1 DMs and servers you still access
              </Label>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Include these for reference and export when you still need the
                JSON, otherwise leave them hidden so the safe flow focuses on
                server/group messages you can no longer access.
              </p>
            </div>
          </div>
        </CardSection>
        <div className="space-y-4 p-10 text-left">
          <h3 className="font-lato text-xl font-medium text-slate-900 dark:text-slate-100">
            What this will do
          </h3>
          <ul className="font-lato space-y-4 text-sm text-slate-700 dark:text-slate-300">
            <li className="flex items-center gap-3">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-600" />
              <span>
                Create deletion requests for server messages you can no longer
                access
              </span>
            </li>
            <li className="flex items-center gap-3">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-600" />
              <span>
                Create deletion requests for group DM messages you've left
              </span>
            </li>
            <li className="flex items-center gap-3">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-500" />
              <span>
                1:1 DMs will remain untouched (Discord won't delete these
                anyway)
              </span>
            </li>
          </ul>
        </div>

        <CardSection withBorder>
          <h4 className="font-lato mb-3 text-sm font-medium text-slate-900 dark:text-slate-100">
            Open the extracted Messages folder
          </h4>
          <MessagesFolderSelector
            loading={loading}
            error={error}
            onSelectFolder={onSelectFolder}
            description="Select the extracted Messages folder so we can index everything locally."
          />
        </CardSection>
      </Card>
    </div>

    <div className="flex justify-center">
      <BackButton onClick={onBack} />
    </div>
  </PageContainer>
);

const RiskyFlowView: React.FC<FlowViewProps> = ({
  onBack,
  onSelectFolder,
  loading,
  error,
  preferences,
  onToggleAccessible,
}) => {
  const [step, setStep] = useState<RiskyStep>("warning");

  const setupSteps: SetupStep[] = [
    {
      title: "Use Brave browser (recommended)",
      description:
        "Violentmonkey is no longer supported on Chrome due to its Manifest V2 architecture. As an alternative, consider using Brave browser which is also open source and Chromium-based.",
    },
    {
      title: "Install Violentmonkey",
      description: "This extension allows running the deletion scripts",
      actionLabel: "Install Violentmonkey",
      actionUrl:
        "https://chrome.google.com/webstore/detail/violentmonkey/jinjaccalgkegednnccohejagnlnfdag",
    },
    {
      title: "Install Vencord userscript",
      description: "This provides the foundation for Discord modifications",
      actionLabel: "Install Vencord",
      actionUrl:
        "https://raw.githubusercontent.com/Vencord/builds/main/Vencord.user.js",
    },
    {
      title: "Install DiscorchDeleter userscript",
      description: "This is the actual deletion automation tool",
      actionLabel: "Install DiscorchDeleter",
      actionUrl:
        "https://github.com/Darker-Ink/DiscorchDeleter/releases/download/v1.1.0/discorchdeleter.user.js",
    },
  ];

  if (step === "warning") {
    return (
      <PageContainer>
        <IconHeader
          icon={
            <Lightning
              size={48}
              className="mx-auto text-slate-600 dark:text-slate-400"
            />
          }
          title="Proceed with caution"
          subtitle="Automating 1:1 DM deletion violates Discord's terms of service"
          centered
        />

        <Card className="mx-auto w-full max-w-xl">
          <CardSection className="space-y-3 border-b border-slate-200 text-left dark:border-slate-700">
            <div className="flex items-center gap-3">
              <Checkbox
                id="risky-include-accessible"
                checked={preferences.includeAccessibleResults}
                onCheckedChange={(checked) => onToggleAccessible(!!checked)}
              />
              <div>
                <Label
                  htmlFor="risky-include-accessible"
                  className="text-sm font-medium text-slate-900 dark:text-slate-100"
                >
                  Include servers and group chats
                </Label>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  Keep these exports for reference. Toggle off to focus strictly
                  on 1:1 DMs and left-server automation.
                </p>
              </div>
            </div>
          </CardSection>
          <InfoSection
            heading="How this works"
            headingLevel="h3"
            className="text-left"
          >
            <p>
              This method hooks into Discord's web client and uses the exact
              same deletion code that runs when you manually press delete on a
              message. It respects Discord's rate limits and does not access
              your authentication token directly.
            </p>
            <p>
              While this violates Discord's terms of service, we have not
              observed any account restrictions in practice. This is considered
              the safest automation method available, but use your own
              judgement.
            </p>
            <p>
              Requires installing browser extensions and userscripts. The setup
              process is guided in the next steps.
            </p>
            <p>
              This approach does violate the terms, but it still keeps the
              automation within Discord's own client code. We have not observed
              any account restrictions in practice, but use your own judgement
              when proceeding with this flow.
            </p>
          </InfoSection>
        </Card>

        <div className="flex justify-center gap-6">
          <button
            onClick={onBack}
            className="font-lato cursor-pointer rounded-lg border-2 border-slate-300 px-8 py-3 text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Take me back
          </button>
          <button
            onClick={() => setStep("setup")}
            className="font-lato cursor-pointer rounded-lg bg-red-600 px-8 py-3 text-white transition-colors hover:bg-red-700"
          >
            I understand the risks
          </button>
        </div>
      </PageContainer>
    );
  }

  if (step === "setup") {
    return (
      <PageContainer>
        <IconHeader
          icon={
            <Lightning
              size={48}
              className="mx-auto text-slate-600 dark:text-slate-400"
            />
          }
          title="Setup required tools"
          subtitle="Follow these steps to enable automated DM deletion"
          centered
        />

        <div className="mx-auto w-full max-w-xl">
          <Card className="mx-auto w-full max-w-xl">
            <CardSection className="text-left">
              <div className="space-y-6 text-left">
                {setupSteps.map((stepItem, index) => (
                  <SetupStepItem
                    key={index}
                    step={stepItem}
                    stepNumber={index + 1}
                  />
                ))}

                <div className="space-y-3 text-left">
                  <h3 className="font-lato text-lg font-medium text-slate-900 dark:text-slate-100">
                    Step 5: Open Messages folder and index
                  </h3>
                  <p className="font-lato text-sm text-slate-700 dark:text-slate-300">
                    Point the picker at the extracted Messages folder so we can
                    build the dataset the userscript relies on.
                  </p>
                  <MessagesFolderSelector
                    loading={loading}
                    error={error}
                    onSelectFolder={onSelectFolder}
                    description="After installing the browser tooling, open the extracted Messages folder via the file picker so we can build the dataset the userscript relies on."
                  />
                </div>
              </div>
            </CardSection>
          </Card>
        </div>

        <div className="flex justify-center">
          <BackButton onClick={onBack} />
        </div>
      </PageContainer>
    );
  }

  return null;
};

const MessagesFolderSelector: React.FC<{
  loading: boolean;
  error: string | null;
  onSelectFolder: () => Promise<void>;
  description: string;
}> = ({ loading, error, onSelectFolder, description }) => (
  <div className="flex flex-col items-start space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-6 text-left dark:border-slate-700 dark:bg-slate-900">
    <p className="text-sm text-slate-700 dark:text-slate-300">{description}</p>
    <button
      type="button"
      onClick={onSelectFolder}
      disabled={loading}
      className="inline-flex min-w-[220px] items-center justify-center rounded-md border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-900 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800/60"
    >
      {loading ? "Scanning Messages..." : "Select Messages folder"}
    </button>
    {error && (
      <p className="text-sm font-medium text-red-600 dark:text-red-400">
        {error}
      </p>
    )}
  </div>
);
